// @ts-nocheck

import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, FileText, Globe, Loader2, Check, UploadCloud, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ScrollPage } from '@/components/layout/PageContainer';
import { getUser } from '@/utilities/setting';
import { AI_NAME_MAX_LENGTH } from '@/utilities/aiAgentsStore';
import {
  ROUTES,
  KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES,
  KNOWLEDGE_BASE_TYPE_OPTIONS,
  KNOWLEDGE_BASE_ACCEPT,
} from '@/utilities/constant';
import { createKnowledgeBase, checkUrlReachable, listKnowledgeBases } from '@/service/knowledgeBaseApi';
import { validateKnowledgeBaseFile } from '@/utilities/knowledgeBaseFile';
import './builder-v2/ui/studio.css';
import './kbcreate.css';

const SOURCE_TYPES = [
  { value: 'file', label: 'Upload file', hint: 'PDF, DOC, DOCX and TXT documents' },
  { value: 'url', label: 'Website / URL', hint: 'Crawl and ingest a public URL' },
];

const MAX_FILE_LABEL = `${(KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES / 1024 / 1024).toFixed(0)} MB`;


const URL_REGEX =
  /^https?:\/\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?::\d{1,5})?(?:[/?#]\S*)?$/;

const DUPLICATE_NAME_MESSAGE =
  'A knowledge base with this name already exists. Please choose a different name.';


const rowName = (row: any) => String(row?.name ?? row?.kb_name ?? '').trim().toLowerCase();

const CreateKnowledgeBasePage = () => {
  const navigate = useNavigate();
  const user = getUser();

  const [kbName, setKbName] = useState('');
  const [kbType, setKbType] = useState(KNOWLEDGE_BASE_TYPE_OPTIONS[0]?.value || 'general');
  const [sourceType, setSourceType] = useState('file');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const backTo = `/${ROUTES.AI_AGENT.KNOWLEDGE_BASE_LIST}`;

  const validate = () => {
    const next: any = {};
    if (!kbName.trim()) next.kbName = 'Knowledge base name is required';
    else if (kbName.trim().length > AI_NAME_MAX_LENGTH)
      next.kbName = `Name must be ${AI_NAME_MAX_LENGTH} characters or fewer`;
    if (sourceType === 'url') {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) next.url = 'Enter a URL to ingest';
      else if (!URL_REGEX.test(trimmedUrl)) next.url = 'Enter a valid URL, e.g. https://example.com';
    }
    if (sourceType === 'file' && file.length === 0) next.file = 'Choose a file to upload';
    setErrors(next);
    return Object.keys(next).length === 0;
  };


  const handleFileChange = async (files: File[]) => {
    if (!files || files.length === 0) {
      setFile([]);
      return;
    }
    const fileError = await validateKnowledgeBaseFile(files[0]);
    if (fileError) {
      setFile([]);
      setErrors((prev: any) => ({ ...prev, file: fileError }));
      toast.error(fileError);
      return;
    }
    setFile(files);
    setErrors((prev: any) => ({ ...prev, file: undefined }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validate()) return;

    if (sourceType === 'file') {
      const fileError = await validateKnowledgeBaseFile(file[0]);
      if (fileError) {
        setErrors((prev: any) => ({ ...prev, file: fileError }));
        toast.error(fileError);
        return;
      }
    }

 
    setCheckingUrl(true); 
    try {
      const existing = await listKnowledgeBases();
      const wanted = kbName.trim().toLowerCase();
      if (existing.some((row: any) => rowName(row) === wanted)) {
        setErrors((prev: any) => ({ ...prev, kbName: DUPLICATE_NAME_MESSAGE }));
        toast.error(DUPLICATE_NAME_MESSAGE);
        setCheckingUrl(false);
        return;
      }
    } catch {
    
    }
    setCheckingUrl(false);

    if (sourceType === 'url') {
      setCheckingUrl(true);
      try {
        await checkUrlReachable(url.trim());
      } catch (err: any) {
        setErrors((prev: any) => ({
          ...prev,
          url: typeof err === 'string' ? err : 'Wrong URL entered — this URL could not be reached.',
        }));
        toast.error(typeof err === 'string' ? err : 'Wrong URL entered — this URL could not be reached.');
        setCheckingUrl(false);
        return;
      }
      setCheckingUrl(false);
    }

    setSaving(true);
    try {
      await createKnowledgeBase({
        kbName: kbName.trim(),
        userId: user?.id,
        kbType: kbType || undefined,
        resellerId: user?.company_id ? String(user.company_id) : undefined,
        url: sourceType === 'url' ? url.trim() : undefined,
        file: sourceType === 'file' ? file[0] : undefined,
      });
      toast.success('Knowledge base creation started');
      navigate(backTo);
    } catch (err: any) {
      const message = typeof err === 'string' ? err : 'Failed to create knowledge base';

      if (/already (taken|exists)/i.test(message)) {
        setErrors((prev: any) => ({ ...prev, kbName: message }));
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const ACCEPT_EXT = String(KNOWLEDGE_BASE_ACCEPT || '')
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.startsWith('.'));

  const pickFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    const f = incoming[0];
    if (ACCEPT_EXT.length && !ACCEPT_EXT.some((ext) => f.name.toLowerCase().endsWith(ext))) {
      const label = ACCEPT_EXT.map((e) => e.slice(1).toUpperCase()).join(', ');
      setErrors((prev: any) => ({ ...prev, file: `${f.name} is not allowed. Only ${label} files are accepted.` }));
      return;
    }
    if (f.size > KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES) {
      setErrors((prev: any) => ({ ...prev, file: `${f.name} exceeds ${MAX_FILE_LABEL} limit` }));
      return;
    }
    handleFileChange([f]);
  };

  const fmtBytes = (n: number) =>
    n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

  const nameDone = kbName.trim().length > 0 && !errors.kbName;
  const sourceDone = sourceType === 'file' ? file.length > 0 : URL_REGEX.test(url.trim());
  const stepsDone = (nameDone ? 1 : 0) + (sourceDone ? 1 : 0);
  const stepsLeft = 2 - stepsDone;
  const busy = saving || checkingUrl;
  const typeLabel = KNOWLEDGE_BASE_TYPE_OPTIONS.find((o) => o.value === kbType)?.label || 'General';
  const nameBad = !!errors.kbName;
  const sourceBad = !!(errors.url || errors.file);
  const secState = (done: boolean, bad: boolean) => (done && !bad ? 'ags-ok' : bad ? 'ags-bad' : '');
  const tag = (done: boolean, bad: boolean) =>
    done && !bad ? <span className="ags-st ags-done">Done</span> : bad ? <span className="ags-st ags-err">Fix</span> : <span className="ags-reqpill"><b aria-hidden>!</b> Required</span>;

  return (
    <ScrollPage>
      <div className="ags ags-kbpage">
        <form className="ags-kbwrap" onSubmit={handleSubmit} noValidate>
          <div className="ags-kbhead">
            <Link to={backTo} className="ags-back" aria-label="Back to knowledge base">
              <ArrowLeft className="ags-i" />
            </Link>
            <div>
              <h1>New knowledge base</h1>
              <p>Give your AI agent a document or website to answer from.</p>
            </div>
          </div>

          <div className="ags-hero">
            <div className="ags-orb">
              <BookOpen style={{ width: 28, height: 28 }} />
            </div>
            <div>
              <h2>{kbName.trim() || 'Untitled knowledge base'}</h2>
              <p>Callers get answers pulled from this source, so keep it current and focused.</p>
              <div className="ags-meta">
                <span>{typeLabel}</span>
                <span>{sourceType === 'file' ? 'File upload' : 'Website'}</span>
                <span className={stepsLeft ? 'ags-miss' : undefined}>
                  {stepsLeft ? `${stepsLeft} ${stepsLeft > 1 ? 'steps' : 'step'} left` : 'Ready to create'}
                </span>
              </div>
            </div>
          </div>

          <section className={`ags-sec ${secState(nameDone, nameBad)}`}>
            <div className="ags-sh">
              <div className="ags-ico">
                <BookOpen className="ags-i" />
              </div>
              <div className="ags-grow">
                <h3>
                  <span className="ags-star" aria-hidden>* </span>Basics
                </h3>
                <p>Name the knowledge base and pick its type.</p>
              </div>
              {tag(nameDone, nameBad)}
            </div>
            <div className="ags-row2">
              <div className="ags-field">
                <label htmlFor="kb-name">
                  Name <span className="ags-r">*</span>
                </label>
                <input
                  id="kb-name"
                  className={`ags-ctl${nameBad ? ' ags-invalid' : ''}`}
                  placeholder="e.g. Support FAQ"
                  value={kbName}
                  maxLength={AI_NAME_MAX_LENGTH}
                  aria-invalid={nameBad}
                  autoComplete="off"
                  onChange={(e) => {
                    setKbName(e.target.value);
                    if (errors.kbName) setErrors((prev: any) => ({ ...prev, kbName: undefined }));
                  }}
                />
                <div className="ags-cnt">
                  <span style={{ color: 'var(--red)' }}>{errors.kbName || ''}</span>
                  <span>
                    {kbName.length} of {AI_NAME_MAX_LENGTH}
                  </span>
                </div>
              </div>
              <div className="ags-field">
                <label htmlFor="kb-type">Type</label>
                <select id="kb-type" className="ags-ctl" value={kbType} onChange={(e) => setKbType(e.target.value)}>
                  {KNOWLEDGE_BASE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="ags-cnt">
                  <span>Helps the agent pick the right tone.</span>
                </div>
              </div>
            </div>
          </section>

          <section className={`ags-sec ${secState(sourceDone, sourceBad)}`}>
            <div className="ags-sh">
              <div className="ags-ico">
                <UploadCloud className="ags-i" />
              </div>
              <div className="ags-grow">
                <h3>
                  <span className="ags-star" aria-hidden>* </span>Source
                </h3>
                <p>Feed it from one file or one public URL.</p>
              </div>
              {tag(sourceDone, sourceBad)}
            </div>

            <div className="ags-kbopts" role="radiogroup" aria-label="Source type">
              {SOURCE_TYPES.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={sourceType === opt.value}
                  className="ags-kbopt"
                  onClick={() => {
                    setSourceType(opt.value);
                    setErrors((e: any) => ({ ...e, url: undefined, file: undefined }));
                  }}
                >
                  <span className="ags-dot">
                    {opt.value === 'file' ? <FileText className="ags-i" /> : <Globe className="ags-i" />}
                  </span>
                  <span>
                    <b>{opt.label}</b>
                    <small>{opt.hint}</small>
                  </span>
                </button>
              ))}
            </div>

            {sourceType === 'url' ? (
              <div className="ags-field" style={{ marginBottom: 0 }}>
                <label htmlFor="kb-url">
                  URL <span className="ags-r">*</span>
                </label>
                <input
                  id="kb-url"
                  type="url"
                  inputMode="url"
                  className={`ags-ctl${errors.url ? ' ags-invalid' : ''}`}
                  placeholder="https://example.com/help"
                  value={url}
                  autoComplete="off"
                  onChange={(e) => {
                    setUrl(e.target.value);
                    if (errors.url) setErrors((prev: any) => ({ ...prev, url: undefined }));
                  }}
                />
                <div className="ags-cnt">
                  <span style={{ color: 'var(--red)' }}>{errors.url || ''}</span>
                  <span>Public pages only.</span>
                </div>
              </div>
            ) : (
              <div className="ags-field" style={{ marginBottom: 0 }}>
                <label>
                  File <span className="ags-r">*</span>
                </label>
                <button
                  type="button"
                  className={`ags-kbdrop${dragOver ? ' ags-over' : ''}${errors.file ? ' ags-invalid' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    pickFiles(e.dataTransfer.files);
                  }}
                >
                  <UploadCloud className="ags-i" style={{ width: 26, height: 26, color: 'var(--muted)' }} />
                  <b>Drop a file here, or click to browse</b>
                  <span className="ags-hint">{`PDF, DOC, DOCX or TXT, up to ${MAX_FILE_LABEL}`}</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={KNOWLEDGE_BASE_ACCEPT}
                  onChange={(e) => {
                    pickFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                {errors.file && (
                  <div className="ags-msg ags-err ags-show">
                    <AlertCircle className="ags-i" />
                    <span>{errors.file}</span>
                  </div>
                )}
                {file.length > 0 && (
                  <div className="ags-kbfile">
                    <FileText className="ags-i" />
                    <div className="ags-nm">
                      <b>{file[0].name}</b>
                      <small>{fmtBytes(file[0].size)}</small>
                    </div>
                    <button type="button" className="ags-x" aria-label={`Remove ${file[0].name}`} onClick={() => setFile([])}>
                      <X className="ags-i" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>

          <div className="ags-kbdock">
            <div className="ags-sp">
              <b>{stepsDone} of 2</b> required steps done
            </div>
            <button type="button" className="ags-btn" onClick={() => navigate(backTo)} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="ags-btn ags-primary" disabled={busy}>
              {busy ? <Loader2 className="ags-i animate-spin" /> : <Check className="ags-i" />}
              {checkingUrl ? 'Checking…' : saving ? 'Creating…' : 'Create knowledge base'}
            </button>
          </div>
        </form>
      </div>
    </ScrollPage>
  );
};

export default CreateKnowledgeBasePage;
